/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Passport Translation Service
 *
 * Provides runtime translation of schema-driven passport field labels, descriptions,
 * and enum display values, fetching structured TranslationEntry dictionaries from the
 * passport backend and caching them in memory.
 *
 * Key Features:
 * - Lazy-loading and in-memory caching of schema-language translation maps
 * - Path-based translation with hierarchical candidate resolution
 * - Bounded LRU-style cache eviction to prevent unbounded memory growth
 * - Graceful fallback to input key or path when no translation is found
 * - Deduplication of concurrent HTTP requests via `shareReplay`
 *
 * Related Modules:
 * - PassportModule
 * - Schema Definitions API (`/definitions/schema/:schemaId/languages/:languageCode`)
 *
 * Implementation Notes:
 * - `load()` must be called before any translate method for a given `schemaId`
 * - The observable cache uses insertion order for eviction; the oldest entry is removed when the
 *   cache reaches `MAX_CACHE_SIZE`
 * - Array index markers (e.g. `[0]`, `[2]`) in paths are stripped before key lookup
 *
 * Example Usage:
 * ```typescript
 * translationService.load('battery-v1', 'de').subscribe(() => {
 *   const label = translationService.translateByPath('properties.capacity', 'battery-v1');
 * });
 * ```
 */

import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, shareReplay, tap} from 'rxjs';
import {environment} from '../environments/environment';
import {TranslationEntry} from '@product-passport/passport/dto';
import {SchemaLanguageResponse} from '../models/Schema';

const PASSPORT_HOST = environment.backend.passport_host;

/**
 * Angular service that loads, caches, and resolves translations for schema-driven passport views.
 *
 * Translations are fetched once per schema-language pair from the passport backend and stored
 * in two complementary maps: an Observable cache that deduplicates in-flight HTTP requests, and
 * a resolved-value map used for synchronous lookups after loading completes.
 *
 * Inject this service into any component or resolver that needs to display localised passport
 * field labels or values based on a specific schema definition.
 */
@Injectable({providedIn: 'root'})
export class PassportTranslationService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<
    string,
    Observable<Record<string, TranslationEntry>>
  >();
  private readonly translations = new Map<
    string,
    Record<string, TranslationEntry>
  >();
  private readonly MAX_CACHE_SIZE = 50;

  /**
   * Loads and caches translations for a schema-language pair.
   *
   * Fetches the translation dictionary from the passport backend (if not already cached) and
   * stores the resolved map in the internal translations store so that subsequent calls to
   * `translate()`, `translateByPath()`, `translateInfoByPath()`, and `translateValueByPath()`
   * can resolve values synchronously.
   *
   * @param {string} schemaId - Schema identifier used by the backend definitions API.
   * @param {string} languageCode - BCP 47 language code (for example, `en`, `de`).
   * @returns {Observable<void>} Observable that completes once the in-memory translation map is updated.
   */
  load(schemaId: string, languageCode: string): Observable<void> {
    return this.getTranslations(schemaId, languageCode).pipe(
      tap((t) => this.translations.set(schemaId, t)),
      map(() => undefined),
    );
  }

  /**
   * Translates a direct translation key for a loaded schema, returning the entry label.
   *
   * Performs an exact key lookup in the resolved translation map for the given schema.
   * Use this method when the caller already knows the exact translation key.
   * For path-based resolution with fallback hierarchy, use `translateByPath()` instead.
   *
   * @param {string} key - Translation key to resolve.
   * @param {string} schemaId - Schema identifier previously loaded via {@link load}.
   * @returns {string} Translated label value, or the original `key` when no translation exists.
   */
  translate(key: string, schemaId: string): string {
    return this.translations.get(schemaId)?.[key]?.label ?? key;
  }

  /**
   * Translates a field label by using a schema path and optional parent path context.
   *
   * Resolves a translation label by building a list of lookup candidates (most-specific first) and
   * returning the first match found in the schema's translation map. Candidate generation is
   * handled by `getLookupCandidates()` and follows this precedence:
   * 1. `parentPath.path` — fully-qualified composite key
   * 2. `path` — standalone normalized path
   * 3. `parentPath` — parent-only fallback when `path` is empty
   *
   * When no candidate matches, the method returns `fallback` (if provided) or the first
   * normalized candidate key.
   *
   * @param {string} path - Field path for translation lookup (may contain array index markers).
   * @param {string} schemaId - Schema identifier previously loaded via {@link load}.
   * @param {string} [fallback] - Optional explicit fallback value when no candidate matches.
   * @param {string} [parentPath] - Optional parent path used to build hierarchical composite candidates.
   * @returns {string} The first matching translated label, the fallback, or the first candidate key.
   */
  translateByPath(
    path: string,
    schemaId: string,
    fallback?: string,
    parentPath?: string,
  ): string {
    const entry = this.getEntryByPath(path, schemaId, parentPath);
    if (entry?.label) {
      return entry.label;
    }

    if (fallback) {
      return fallback;
    }

    const candidates = this.getLookupCandidates(path, parentPath);
    return (candidates[0] ?? this.normalizeTranslationPath(path)) || path;
  }

  /**
   * Returns the description/tooltip text for a field at the given schema path.
   *
   * @param {string} path - Field path for translation lookup (may contain array index markers).
   * @param {string} schemaId - Schema identifier previously loaded via {@link load}.
   * @param {string} [fallback] - Optional explicit fallback value when no candidate matches.
   * @param {string} [parentPath] - Optional parent path used to build hierarchical composite candidates.
   * @returns {string} The translated info text, the fallback, or an empty string.
   */
  translateInfoByPath(
    path: string,
    schemaId: string,
    fallback?: string,
    parentPath?: string,
  ): string {
    const entry = this.getEntryByPath(path, schemaId, parentPath);
    return entry?.info ?? fallback ?? '';
  }

  /**
   * Returns the translated display string for a raw enum/select value at the given schema path.
   *
   * @param {string} path - Field path for translation lookup (may contain array index markers).
   * @param {string} schemaId - Schema identifier previously loaded via {@link load}.
   * @param {string} rawValue - The raw enum or select value to translate.
   * @param {string} [fallback] - Optional explicit fallback; defaults to `rawValue` when absent.
   * @returns {string} The translated display string, the fallback, or the original `rawValue`.
   */
  translateValueByPath(
    path: string,
    schemaId: string,
    rawValue: string,
    fallback?: string,
  ): string {
    const entry = this.getEntryByPath(path, schemaId);
    return entry?.values?.[rawValue] ?? fallback ?? rawValue;
  }

  /**
   * Resolves the first matching TranslationEntry for the given path and optional parent context.
   *
   * @param {string} path - Field path (may contain array index markers).
   * @param {string} schemaId - Schema identifier previously loaded via {@link load}.
   * @param {string} [parentPath] - Optional parent context path.
   * @returns {TranslationEntry | undefined} The first matching entry, or undefined.
   */
  private getEntryByPath(
    path: string,
    schemaId: string,
    parentPath?: string,
  ): TranslationEntry | undefined {
    const candidates = this.getLookupCandidates(path, parentPath);
    const translationsForSchema = this.translations.get(schemaId) ?? {};
    for (const candidate of candidates) {
      const entry = translationsForSchema[candidate];
      if (entry) {
        return entry;
      }
    }
    return undefined;
  }

  /**
   * Builds de-duplicated lookup candidates for path-based translation.
   *
   * Normalizes both `path` and `parentPath`, then constructs candidates in descending specificity.
   * Duplicate entries are removed via `Set` before returning so the caller can iterate without
   * redundant lookups.
   *
   * @param {string} path - Current field path (raw, may include array index markers).
   * @param {string} [parentPath] - Optional parent context path for composite key generation.
   * @returns {string[]} Ordered candidate keys from most specific to least specific.
   */
  private getLookupCandidates(path: string, parentPath?: string): string[] {
    const normalizedPath = this.normalizeTranslationPath(path);
    const normalizedParentPath = parentPath
      ? this.normalizeTranslationPath(parentPath)
      : '';

    const candidates: string[] = [];

    if (
      normalizedParentPath &&
      normalizedPath &&
      normalizedPath !== normalizedParentPath &&
      !normalizedPath.startsWith(`${normalizedParentPath}.`)
    ) {
      candidates.push(`${normalizedParentPath}.${normalizedPath}`);
    }

    if (normalizedPath) {
      candidates.push(normalizedPath);
    }

    if (!normalizedPath && normalizedParentPath) {
      candidates.push(normalizedParentPath);
    }

    return [...new Set(candidates)];
  }

  /**
   * Normalizes translation paths by removing array/index markers and trimming dots.
   *
   * Iterates the path character-by-character to strip bracket-enclosed segments, then splits
   * on `.` and filters empty segments to produce a clean dot-separated key. This avoids regex
   * backtracking on deeply-nested or malformed paths.
   *
   * @param {string} path - Raw path string used by UI schema traversal.
   * @returns {string} Dot-separated path suitable for translation key lookup.
   *
   * @example
   * normalizeTranslationPath('cells[0].label')    // → 'cells.label'
   * normalizeTranslationPath('items[2][1].name')  // → 'items.name'
   * normalizeTranslationPath('.foo..bar.')         // → 'foo.bar'
   */
  private normalizeTranslationPath(path: string): string {
    let stripped = '';
    let inBracket = false;
    for (const char of path) {
      if (char === '[') {
        inBracket = true;
      } else if (char === ']') {
        inBracket = false;
      } else if (!inBracket) {
        stripped += char;
      }
    }
    return stripped.split('.').filter(Boolean).join('.');
  }

  /**
   * Returns cached translations or fetches them from the passport backend.
   *
   * On the first request for a schema-language pair the method creates a new Observable backed
   * by an HTTP GET and stores it in the Observable cache with `shareReplay` so that concurrent
   * subscribers receive the same response without triggering additional network requests.
   * When the cache exceeds `MAX_CACHE_SIZE`, the oldest insertion-order entry is evicted first.
   *
   * HTTP errors are caught and replaced with an empty dictionary so that the view degrades
   * gracefully instead of propagating an error through the stream.
   *
   * @param {string} schemaId - Schema identifier used by the definitions endpoint.
   * @param {string} languageCode - BCP 47 language code for the requested translations.
   * @returns {Observable<Record<string, TranslationEntry>>} Observable emitting the translation dictionary for the requested pair.
   */
  private getTranslations(
    schemaId: string,
    languageCode: string,
  ): Observable<Record<string, TranslationEntry>> {
    const cacheKey = `${schemaId}:${languageCode}`;
    if (!this.cache.has(cacheKey)) {
      // Evict oldest entry if cache is at max size
      if (this.cache.size >= this.MAX_CACHE_SIZE) {
        const oldestKey = this.cache.keys().next().value;
        if (oldestKey !== undefined) {
          this.cache.delete(oldestKey);
        }
      }

      this.cache.set(
        cacheKey,
        this.http
          .get<SchemaLanguageResponse>(
            `${PASSPORT_HOST}/definitions/schema/${schemaId}/languages/${languageCode}`,
          )
          .pipe(
            map((r) => r.data.translations),
            catchError(() => of({} as Record<string, TranslationEntry>)),
            shareReplay({bufferSize: 1, refCount: true}),
          ),
      );
    }
    const cachedTranslations = this.cache.get(cacheKey);
    return cachedTranslations ?? of({} as Record<string, TranslationEntry>);
  }
}
